import type { CanFrame } from '../canlog/types';

const BYTE_COUNT = 8;
const MAX_POINTS = 1200;

export type ByteTuple = [
  number | null,
  number | null,
  number | null,
  number | null,
  number | null,
  number | null,
  number | null,
  number | null
];

export type GraphPoint = {
  t: number;
  bytes: ByteTuple;
};

export type GraphStats = {
  frameCount: number;
  approxHz: number | null;
  firstTimestampMs: number | null;
  lastTimestampMs: number | null;
  byteMin: Array<number | null>;
  byteMax: Array<number | null>;
};

export type GraphSeries = {
  canId: string;
  points: GraphPoint[];
  stats: GraphStats;
  xMode: 'timestamp' | 'index';
  totalPoints: number;
};

export type CanIdOption = {
  canId: number;
  canIdHex: string;
  frameCount: number;
};

function formatCanId(canId: number) {
  return `0x${canId.toString(16).toUpperCase().padStart(canId > 0x7ff ? 8 : 3, '0')}`;
}

function emptyByteTuple(): ByteTuple {
  return [null, null, null, null, null, null, null, null];
}

function hasUsableTimestamps(frames: CanFrame[]) {
  if (frames.length === 0) return false;
  return frames.every((frame) => Number.isFinite(frame.timestampMs));
}

function downsample<T>(items: T[], maxPoints = MAX_POINTS) {
  if (items.length <= maxPoints) return items;
  const step = Math.ceil(items.length / maxPoints);
  return items.filter((_, index) => index % step === 0).slice(0, maxPoints);
}

export function getCanIdOptions(frames: CanFrame[]): CanIdOption[] {
  const counts = new Map<number, CanIdOption>();
  for (const frame of frames) {
    const existing = counts.get(frame.canId);
    if (existing) {
      existing.frameCount += 1;
    } else {
      counts.set(frame.canId, {
        canId: frame.canId,
        canIdHex: frame.canIdHex || formatCanId(frame.canId),
        frameCount: 1
      });
    }
  }

  return Array.from(counts.values()).sort((a, b) => b.frameCount - a.frameCount || a.canId - b.canId);
}

export function getMostFrequentCanId(frames: CanFrame[]) {
  return getCanIdOptions(frames)[0]?.canId ?? null;
}

export function buildGraphSeries(frames: CanFrame[], selectedCanId: number | null): GraphSeries | null {
  if (selectedCanId === null) return null;
  const matchingFrames = frames.filter((frame) => frame.canId === selectedCanId);
  if (matchingFrames.length === 0) return null;

  const useTimestamp = hasUsableTimestamps(matchingFrames);
  const firstTimestampMs = useTimestamp ? matchingFrames[0].timestampMs : null;
  const lastTimestampMs = useTimestamp ? matchingFrames.at(-1)?.timestampMs ?? null : null;
  const durationMs = firstTimestampMs !== null && lastTimestampMs !== null ? Math.max(0, lastTimestampMs - firstTimestampMs) : 0;
  const byteMin = Array.from<number | null>({ length: BYTE_COUNT }).fill(null);
  const byteMax = Array.from<number | null>({ length: BYTE_COUNT }).fill(null);

  const allPoints = matchingFrames.map((frame, index) => {
    const bytes = emptyByteTuple();
    for (let byteIndex = 0; byteIndex < BYTE_COUNT; byteIndex += 1) {
      const value = frame.data[byteIndex];
      if (typeof value !== 'number') continue;
      bytes[byteIndex] = value;
      byteMin[byteIndex] = byteMin[byteIndex] === null ? value : Math.min(byteMin[byteIndex] as number, value);
      byteMax[byteIndex] = byteMax[byteIndex] === null ? value : Math.max(byteMax[byteIndex] as number, value);
    }

    return {
      t: useTimestamp ? frame.timestampMs : index,
      bytes
    };
  });

  return {
    canId: matchingFrames[0].canIdHex || formatCanId(selectedCanId),
    points: downsample(allPoints),
    totalPoints: allPoints.length,
    xMode: useTimestamp ? 'timestamp' : 'index',
    stats: {
      frameCount: matchingFrames.length,
      approxHz: durationMs > 0 ? matchingFrames.length / (durationMs / 1000) : null,
      firstTimestampMs,
      lastTimestampMs,
      byteMin,
      byteMax
    }
  };
}
